import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  ymrsAssessments: [],
  currentYmrsAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all YMRS Assessments
export const fetchYmrsAssessments = createAsyncThunk(
  'ymrsAssessments/fetchYmrsAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/ymrs-assessments?patient_id=${patientId}`
        : '/ymrs-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch YMRS assessments'
      );
    }
  }
);

// Fetch single YMRS Assessment
export const fetchYmrsAssessment = createAsyncThunk(
  'ymrsAssessments/fetchYmrsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ymrs-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch YMRS assessment'
      );
    }
  }
);

// Create YMRS Assessment
export const createYmrsAssessment = createAsyncThunk(
  'ymrsAssessments/createYmrsAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ymrs-assessments', assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create YMRS assessment'
      );
    }
  }
);

// Update YMRS Assessment
export const updateYmrsAssessment = createAsyncThunk(
  'ymrsAssessments/updateYmrsAssessment',
  async ({ case_id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ymrs-assessments/case/${case_id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update YMRS assessment'
      );
    }
  }
);

// Delete YMRS Assessment
export const deleteYmrsAssessment = createAsyncThunk(
  'ymrsAssessments/deleteYmrsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/ymrs-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete YMRS assessment'
      );
    }
  }
);

// ==================== Slice ====================
const ymrsAssessmentsSlice = createSlice({
  name: 'ymrsAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentYmrsAssessment: (state, action) => {
      state.currentYmrsAssessment = action.payload;
    },
    clearCurrentYmrsAssessment: (state) => {
      state.currentYmrsAssessment = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchYmrsAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchYmrsAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ymrsAssessments = action.payload;
      })
      .addCase(fetchYmrsAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchYmrsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchYmrsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentYmrsAssessment = action.payload;
      })
      .addCase(fetchYmrsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createYmrsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createYmrsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.ymrsAssessments.unshift(action.payload);
      })
      .addCase(createYmrsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateYmrsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateYmrsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        const updated = action.payload.data ?? action.payload;
        const index = state.ymrsAssessments.findIndex(
          (item) => item.id === updated.id
        );

        if (index !== -1) {
          state.ymrsAssessments[index] = updated;
        }

        if (state.currentYmrsAssessment?.id === updated.id) {
          state.currentYmrsAssessment = updated;
        }
      })
      .addCase(updateYmrsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteYmrsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteYmrsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;

        state.ymrsAssessments = state.ymrsAssessments.filter(
          (item) => item.id !== action.payload
        );

        if (state.currentYmrsAssessment?.id === action.payload) {
          state.currentYmrsAssessment = null;
        }
      })
      .addCase(deleteYmrsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentYmrsAssessment,
  clearCurrentYmrsAssessment,
} = ymrsAssessmentsSlice.actions;

export default ymrsAssessmentsSlice.reducer;
